import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Megaphone } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { sponsored_business: "Sponsored Business", sponsored_map: "Sponsored Map", sponsored_mission: "Sponsored Mission", featured_deal: "Featured Deal" };
const PRICE_LABEL = { sponsored_business_week: "Sponsored Business (€/sett)", sponsored_map_week: "Sponsored Map (€/sett)", sponsored_mission: "Sponsored Mission (€)", featured_deal_week: "Featured Deal (€/sett)" };

export default function AdvertisingCeoPanel() {
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);

  const load = () => {
    api.get("/ceo/ad-campaigns").then(({ data }) => setData(data)).catch(() => {});
    api.get("/ceo/ad-pricing").then(({ data }) => setPricing(data.pricing)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const eur = (v) => "€" + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const savePricing = async () => {
    try { await api.put("/ceo/ad-pricing", { pricing }); toast.success("Prezzi advertising aggiornati"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.patch(`/ceo/ad-campaigns/${id}/status`, { status }); load(); };

  const t = data?.totals || {};
  return (
    <div className="space-y-5" data-testid="advertising-ceo-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#00E676]" /> Advertising Locale</h2>
        <p className="text-sm text-zinc-500">Ricavi pubblicitari e campagne territoriali delle attività.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-[#00E676]/15 to-transparent border border-[#00E676]/30 p-4" data-testid="ad-revenue"><div className="text-xs uppercase tracking-widest text-[#00E676]">Advertising Revenue</div><div className="text-2xl font-black mt-1">{eur(t.revenue)}</div></div>
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-xs uppercase tracking-widest text-zinc-500">Campagne attive</div><div className="text-2xl font-black mt-1">{t.active || 0}</div></div>
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-xs uppercase tracking-widest text-zinc-500">Impression</div><div className="text-2xl font-black mt-1">{t.impressions || 0}</div></div>
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4"><div className="text-xs uppercase tracking-widest text-zinc-500">GMV attribuito</div><div className="text-2xl font-black mt-1">{eur(t.attributed_gmv)}</div></div>
      </div>

      {pricing && (
        <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5">
          <div className="text-sm font-semibold mb-3">Listino prezzi (fissi, CEO-editabili)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.keys(PRICE_LABEL).map((k) => (
              <div key={k} data-testid={`ad-price-${k}`}><Label className="text-xs">{PRICE_LABEL[k]}</Label><Input type="number" step="0.5" value={pricing[k]} onChange={(e) => setPricing((p) => ({ ...p, [k]: parseFloat(e.target.value) || 0 }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
            ))}
          </div>
          <Button data-testid="ad-save-pricing" onClick={savePricing} className="bg-[#00E676] text-black rounded-md mt-4">Salva prezzi</Button>
          <p className="text-xs text-zinc-500 mt-2">DB predisposto per modelli CPC / CPM / CPA e credito pubblicitario (evoluzione futura).</p>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-sm font-semibold">Campagne</div>
        {(data?.campaigns || []).map((c) => (
          <div key={c.id} data-testid={`ceo-ad-${c.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold flex items-center gap-2">{TYPE_LABEL[c.type] || c.type}
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${c.effective_status === "active" ? "bg-[#00E676]/20 text-[#00E676]" : "bg-white/10 text-zinc-400"}`}>{c.effective_status}</span></div>
              <div className="text-xs text-zinc-500 mt-0.5">{c.business_name} · {eur(c.revenue)} · {c.analytics.impressions} imp · {c.analytics.clicks} click · {c.analytics.orders} ord · ROAS {c.analytics.roas}x</div>
            </div>
            <div className="flex items-center gap-1">
              {c.status === "pending_review" && <Button size="sm" data-testid={`ad-approve-${c.id}`} onClick={() => setStatus(c.id, "active")} className="bg-[#00E676] text-black rounded-md h-8 text-xs">Approva</Button>}
              <Button size="sm" variant="outline" onClick={() => setStatus(c.id, c.status === "paused" ? "active" : "paused")} className="rounded-md h-8 text-xs border-white/20">{c.status === "paused" ? "Riattiva" : "Sospendi"}</Button>
            </div>
          </div>
        ))}
        {data && (data.campaigns || []).length === 0 && <div className="text-zinc-500 py-6 text-center text-sm">Nessuna campagna.</div>}
      </div>
    </div>
  );
}
